import React, {ReactNode, useCallback, useId, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Button} from '@/components/app/button';
import {confirm} from '@/components/app/confirmation';
import {DataGrid} from '@/components/app/data-grid';
import {Modal} from '@/components/app/modal';
import {useDebouncedState} from '@/app/hooks/useTimers';
import {Input} from '@/components/ui/input';
import {LoadingSkeleton} from '@/components/app/feedback';
import {ContentHints} from '@/components/app/hint';
import {HierarchyNode} from '@/components/app/hierarchy';
import {HierarchyTable} from '@/components/app/data-grid';
import {cn} from '@/lib/utils';
import {InputGroup} from '@/components/app/input-group';
import isEqual from 'lodash/isEqual';
import {BiotwinNameForm} from './BiotwinNameForm';
import {BiotwinBasicsForm} from './BiotwinBasicsForm';
import {Biotwin, Condition} from '../types';
import {
  useGetConditionsQuery,
  useGetConditionTypesQuery,
  useLazyGetConditionsDataCountQuery,
  useLazyGetConditionsQuery,
} from '../../../app/services/conditionApi';
import {
  useCreateBiotwinMutation,
  useDeleteBiotwinMutation,
  useUpdateBiotwinMutation,
  useUpdateBulkBiotwinConditionsMutation,
} from '../../../app/services/biotwinApi';
import {toastError} from '../../../app/toast';
import {InfoSection} from '../../../app/components/InfoSection';

import {useVirtualLazyLoad} from '../../../app/hooks/useVirtualLazyLoad';
import {BiotwinBasicsViewer} from './BiotwinBasicsViewer';
import {ConditionTypeTag} from './ConditionTypeTag';
import {useBlockUI} from '../../core/hooks/useBlockUI';
import {TextMatch} from '../../../app/components/TextMatch';
import {getTextMatchNormalizedMatcher, getTextMatchNormalizedText} from '../../../app/utils/miscUtils';
import {Icon} from '../../../app/components/Icon';
import {MatchMode} from '../../../app/types';
import {useDemo} from '../../demo/hooks/useDemo';
import {isCompleteBiotwin} from '../helpers/utils';
import {useIsAdmin} from '../../auth/hooks/useIsAdmin';
export interface BiotwinWizardProps {
  className?: string;
  forcedBiotwinBasicsMessage?: string;
  initialBiotwin?: Biotwin;
  initialStep?: Step;
  noNewBiotwin?: boolean;
  noViewBiotwin?: boolean;
  onCancel: (event: {biotwin?: Biotwin}) => void;
  onFinish: (event: {biotwin: Biotwin}) => void;
  onViewBiotwin?: (event: {biotwin: Biotwin}) => void;
  startingStep?: Step;
  summaryStep?: Step;
  tenantId: number;
  visible?: boolean;
}
enum Step {
  BiotwinName = 1,
  BiotwinBasics,
  FamilyHistory,
  PhysiologyAndLifeFactors,
  Allergy,
  Diagnoses,
  Medications,
  Procedures,
  SymptomsAndFindings,
  Tests,
  Completed,
}
const stepMetadataByIndex: {
  [key in Step]: {
    conditionTypeName: string;
    title: string;
  };
} = {
  [Step.BiotwinName]: {
    conditionTypeName: '',
    title: 'Biotwin Name',
  },
  [Step.BiotwinBasics]: {
    conditionTypeName: '',
    title: 'Biotwin Basics',
  },
  [Step.FamilyHistory]: {
    conditionTypeName: 'Family history',
    title: 'Family History',
  },
  [Step.PhysiologyAndLifeFactors]: {
    conditionTypeName: 'Physiology & Life Factors',
    title: 'Physiology & Life Factors',
  },
  [Step.Allergy]: {
    conditionTypeName: 'Allergy/intolerance',
    title: 'Allergy/Intolerance',
  },
  [Step.Diagnoses]: {
    conditionTypeName: 'Diagnoses',
    title: 'Diagnoses',
  },
  [Step.Medications]: {
    conditionTypeName: 'Medications',
    title: 'Medications',
  },
  [Step.Procedures]: {
    conditionTypeName: 'Procedures',
    title: 'Procedures',
  },
  [Step.SymptomsAndFindings]: {
    conditionTypeName: 'Symptoms & Findings',
    title: 'Symptoms & Findings',
  },
  [Step.Tests]: {
    conditionTypeName: 'Tests',
    title: 'Tests',
  },
  [Step.Completed]: {
    conditionTypeName: '',
    title: 'Completed',
  },
};
export const BiotwinWizard = ({
  className,
  forcedBiotwinBasicsMessage,
  initialBiotwin,
  initialStep,
  noNewBiotwin,
  noViewBiotwin,
  onCancel,
  onFinish,
  onViewBiotwin,
  startingStep = Step.BiotwinName,
  summaryStep = Step.BiotwinBasics,
  tenantId,
  visible,
}: BiotwinWizardProps) => {
  const [biotwin, setBiotwin] = useState<Biotwin>();
  const [currentStep, setCurrentStep] = useState<Step>(Step.BiotwinName);
  const [createBiotwin, {isLoading: isLoadingCreateBiotwin}] = useCreateBiotwinMutation();
  const [deleteBiotwin, {isLoading: isLoadingDeleteBiotwin}] = useDeleteBiotwinMutation();
  const [updateBiotwin, {isLoading: isLoadingUpdateBiotwin}] = useUpdateBiotwinMutation();
  const [updateBulkBiotwinConditions, {isLoading: isLoadingUpdateBulkBiotwinConditions}] =
    useUpdateBulkBiotwinConditionsMutation();
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [dialogShow, setDialogShow] = useState(false);
  const maxVisitedStep = useRef<Step>(Step.BiotwinName);
  const continueAfterSubmit = useRef(true);
  const {isDemo} = useDemo();
  useBlockUI(isLoadingDeleteBiotwin || isLoadingUpdateBulkBiotwinConditions);
  useLayoutEffect(() => {
    if (visible) {
      setBiotwin(initialBiotwin);
      setCurrentStep(initialStep ?? Step.BiotwinName);
      setConditions([]);
      return () => {
        maxVisitedStep.current = Step.BiotwinName;
        setDialogShow(false);
      };
    }
  }, [visible]);
  if (biotwin == null && currentStep !== Step.BiotwinName) {
    setCurrentStep(Step.BiotwinName);
    return null;
  }
  if (biotwin != null && !isCompleteBiotwin(biotwin) && currentStep > Step.BiotwinBasics) {
    setCurrentStep(Step.BiotwinBasics);
    return null;
  }
  maxVisitedStep.current = Math.max(maxVisitedStep.current, currentStep);
  return (
    <Modal
      // The summary panel slides out past the dialog's right edge, so the dialog must not clip it.
      className="shadow-none overflow-visible"
      contentClassName="p-0 h-full overflow-visible rounded-[inherit]"
      onClose={() => close()}
      onShow={() => setDialogShow(true)}
      showHeader={false}
      style={{
        height: '80vh',
        width: '50rem',
      }}
      open={visible}
    >
      {renderSummary()}
      <div className={cn(className, 'h-full flex flex-col bg-white relative shadow-md rounded-[inherit]')}>
        {renderStep(currentStep)}
      </div>
    </Modal>
  );
  function renderStep(step: Step) {
    switch (step) {
      case Step.BiotwinName:
        return renderStepBiotwinName();
      case Step.BiotwinBasics:
        return renderStepBiotwinBasics();
      case Step.FamilyHistory:
        return renderStepFamilyHistory();
      case Step.PhysiologyAndLifeFactors:
        return renderStepPhysiologyAndLifeFactors();
      case Step.Allergy:
        return renderStepAllergy();
      case Step.Diagnoses:
        return renderStepDiagnoses();
      case Step.Medications:
        return renderStepMedications();
      case Step.Procedures:
        return renderStepProcedures();
      case Step.SymptomsAndFindings:
        return renderStepSymptomsAndFindings();
      case Step.Tests:
        return renderStepTests();
      case Step.Completed:
        return renderStepCompleted();
      default:
        return null;
    }
  }
  function renderStepBiotwinName() {
    return (
      <>
        {renderHeader({
          description: 'Every biotwin has a name. It can be anything you want. You can change it later.',
          title: `Step ${Step.BiotwinName}: Biotwin Name`,
        })}
        <div className="flex-1 min-h-0 overflow-auto p-6">
          <BiotwinNameForm
            defaultValues={biotwin}
            id={`biotwin-wizard-name-form`}
            onSubmit={async ({name}) => {
              if (biotwin == null) {
                try {
                  const result = await createBiotwin({
                    name,
                    tenantId,
                  }).unwrap();
                  setBiotwin(result);
                  next();
                } catch (e) {
                  toastError(e);
                }
              } else if (biotwin.name !== name) {
                try {
                  const result = await updateBiotwin({
                    id: biotwin.id,
                    changes: {
                      name,
                    },
                  }).unwrap();
                  setBiotwin(result);
                  next();
                } catch (e) {
                  toastError(e);
                }
              } else {
                next();
              }
            }}
            submitButton={false}
          />
        </div>
        <footer className="flex items-center p-6 gap-4 bg-zinc-100 rounded-b-[inherit]">
          <Button
            className={cn('ml-auto', 'rounded-full')}
            form={`biotwin-wizard-name-form`}
            key={'step-biotwin-name-button-next'}
            isLoading={isLoadingCreateBiotwin || isLoadingUpdateBiotwin}
            type="submit"
            variant="warning"
            endIcon={<Icon icon={'arrow-right'} />}
          >
            {'Next'}
          </Button>
        </footer>
      </>
    );
  }
  function renderStepBiotwinBasics() {
    if (biotwin == null) {
      return null;
    }
    const forced = Step.BiotwinBasics < startingStep;
    return (
      <>
        {renderHeader({
          description: 'There are four basic information of any biotwin, which are gender, age, height and weight.',
          title: forced ? 'Biotwin Basics' : `Step ${Step.BiotwinBasics - startingStep + 1}: Biotwin Basics`,
        })}
        <div className="flex-1 min-h-0 overflow-auto p-6">
          {forced && (
            <p className="bg-blue-50 text-blue-400 text-center -mt-6 mb-6 p-6 -mx-6">
              <Icon icon="info-circle" />{' '}
              {forcedBiotwinBasicsMessage ?? `It is necessary to fill up the basics before viewing the biotwin.`}
            </p>
          )}
          <BiotwinBasicsForm
            id="biotwin-wizard-basics-form"
            defaultValues={{
              ...biotwin,
              dateOfBirth: biotwin?.dateOfBirth,
              height: biotwin == null || biotwin.height == null || biotwin.height < 0 ? undefined : biotwin.height,
              heightUnit: biotwin?.heightUnit ?? 'cm',
              weight: biotwin == null || biotwin.weight == null || biotwin.weight < 0 ? undefined : biotwin.weight,
              weightUnit: biotwin?.weightUnit ?? 'kg',
            }}
            onSubmit={async (fieldValues) => {
              if (isEqual(biotwin, fieldValues)) {
                next();
              } else {
                try {
                  const result = await updateBiotwin({
                    id: biotwin.id,
                    changes: {
                      ...fieldValues,
                      name: undefined,
                    },
                  }).unwrap();
                  setBiotwin(result);
                  if (continueAfterSubmit.current) {
                    next();
                  } else {
                    onFinish({
                      biotwin: result,
                    });
                  }
                } catch (e) {
                  toastError(e);
                }
              }
            }}
            submitButton={false}
          />
        </div>
        <footer className="flex items-center p-6 gap-4 bg-zinc-100 rounded-b-[inherit]">
          {hasPrev(Step.BiotwinBasics) && (
            <Button
              onClick={() => prev()}
              type="button"
              variant="text"
              className="rounded-full"
              startIcon={<Icon icon={'arrow-left'} />}
            >
              {'Back'}
            </Button>
          )}
          {forced && (
            <Button
              className={cn('text-muted-foreground ml-auto', 'rounded-full')}
              form="biotwin-wizard-basics-form"
              isLoading={isLoadingUpdateBiotwin && continueAfterSubmit.current}
              onClick={() => (continueAfterSubmit.current = true)}
              type={'submit'}
              variant="ghost"
              endIcon={<Icon icon={'magic'} />}
            >
              {'Continue with biotwin wizard'}
            </Button>
          )}
          <Button
            className={cn(forced ? '' : 'ml-auto', 'rounded-full')}
            form="biotwin-wizard-basics-form"
            isLoading={isLoadingUpdateBiotwin && (!forced || !continueAfterSubmit.current)}
            onClick={() => (continueAfterSubmit.current = !forced)}
            type="submit"
            variant="warning"
            endIcon={<Icon icon={forced ? undefined : 'arrow-right'} />}
          >
            {forced ? 'Save' : 'Next'}
          </Button>
        </footer>
      </>
    );
  }
  function renderStepFamilyHistory() {
    return renderConditionSelectionStep({
      description: (
        <>
          Family disease history is as valuable as sophisticated genetic tests in determining lifelong risks.
          <br />
          Items such as{' '}
          <span className="px-1 bg-white rounded-md inline-block">
            <Icon icon="search" className="text-sm" /> <strong>family descent</strong>
          </span>{' '}
          <span className="px-1 bg-white rounded-md inline-block">
            <Icon icon="search" className="text-sm" /> <strong>born to a mother with</strong> <em>a disease</em>
          </span>{' '}
          or{' '}
          <span className="px-1 bg-white rounded-md inline-block">
            <Icon icon="search" className="text-sm" /> <strong>maternal use of</strong> <em>a medication</em>
          </span>{' '}
          are also part of the family history. Each item implies an additional recommendation for the corresponding
          biotwin.
        </>
      ),
      step: Step.FamilyHistory,
      tree: true,
    });
  }
  function renderStepPhysiologyAndLifeFactors() {
    return renderConditionSelectionStep({
      description: (
        <>
          In this section, please select your lifestyle preferences, including: <strong>habits & abuses</strong>,{' '}
          <strong>professions</strong>, <strong>country/region of residence</strong>,{' '}
          <strong>gender identities/expressions</strong>, <strong>sexual orientation</strong>,{' '}
          <strong>family planning</strong>, <strong>environmental features</strong>, and more. Please be aware that if
          you can't find a specific preference, it's because there are no available recommendations for it.
        </>
      ),
      step: Step.PhysiologyAndLifeFactors,
      tree: true,
    });
  }
  function renderStepAllergy() {
    return renderConditionSelectionStep({
      description: 'The more information you provide, the more accurate the results will be.',
      step: Step.Allergy,
    });
  }
  function renderStepDiagnoses() {
    return renderConditionSelectionStep({
      description: (
        <>
          At this stage, you can add a disease, whether it's <strong>confirmed</strong> or <strong>suspected</strong>.
          If the added disease is confirmed, you can review the treatment options in the biobook. If it is a suspected
          disease, you can check the diagnostic steps.
        </>
      ),
      step: Step.Diagnoses,
    });
  }
  function renderStepMedications() {
    return renderConditionSelectionStep({
      description: (
        <>
          You can add medicines you're <strong>using now</strong> or <strong>planning to use</strong>. If it's a
          medicine you're already using, you can check how it's doing and if it has side effects. If it's a planned
          medicine, you can see what you need to do before starting it, any risks, and if it might interact with other
          medicines.
        </>
      ),
      step: Step.Medications,
    });
  }
  function renderStepProcedures() {
    return renderConditionSelectionStep({
      description:
        'During this phase, you can include surgical or invasive procedures that have either already been undergone or are planned. If the procedure is planned, please also add the perioperative or periprocedural setting. This will allow you to examine the necessary steps during the perioperative or periprocedural period, any associated risks, as well as potential interactions with other biotwin features such as medications.',
      step: Step.Procedures,
    });
  }
  function renderStepSymptomsAndFindings() {
    return renderConditionSelectionStep({
      description:
        'In this section, you can add current complaints (symptoms), physical examination findings (signs), or other features like infectious exposure, feelings, and more.',
      step: Step.SymptomsAndFindings,
    });
  }
  function renderStepTests() {
    return renderConditionSelectionStep({
      description:
        'During this step, you can add any lab test result. You also can add a test to learn how to order or interpret it before proceeding. Information regarding the next steps for interpreting the test result.',
      step: Step.Tests,
    });
  }
  function renderStepCompleted() {
    if (biotwin == null) {
      return null;
    }
    return (
      <InfoSection
        actions={[
          {
            props: {
              className: 'w-[14rem] mb-4',
              children: 'View Biotwin',
              onClick: () =>
                onViewBiotwin?.({
                  biotwin: biotwin,
                }),
              type: 'button',
            },
            type: 'button',
            visible: !noViewBiotwin,
          },
          {
            props: {
              className: 'w-[14rem]',
              children: 'Add New Biotwin',
              onClick: () => {
                setBiotwin(undefined);
                setConditions([]);
                setCurrentStep(Step.BiotwinName);
                maxVisitedStep.current = Step.BiotwinName;
              },
              variant: 'secondary',
              type: 'button',
            },
            type: 'button',
            visible: !noNewBiotwin,
          },
          {
            props: {
              className: 'w-[14rem]',
              children: 'Finish',
              onClick: () =>
                onFinish({
                  biotwin: biotwin,
                }),
              type: 'button',
              variant: 'link',
            },
            type: 'button',
          },
        ]}
        className="h-full p-12"
        icon="check-circle"
        title="Congratulations!"
        subtitle="You have completed entering the main features."
        content={
          <>
            <p className={'leading-normal'}>
              On the biotwin tab, click <Icon icon="chevron-right" /> to the left of the entered condition to provide
              additional details about that condition. You can add more features at any time using the search bar at the
              top of the biotwin tab.
            </p>
          </>
        }
      />
    );
  }
  function renderSummary() {
    return (
      <article className="absolute top-0 bottom-0 left-full w-0 my-6">
        <div
          className={cn(
            'w-[20rem] h-full rounded-r-3xl bg-zinc-100 transition-transform duration-300 flex flex-col',
            !dialogShow || maxVisitedStep.current < summaryStep || currentStep === Step.Completed
              ? '-translate-x-full'
              : 'translate-x-0',
          )}
        >
          {dialogShow && maxVisitedStep.current >= summaryStep && (
            <Summary
              biotwin={biotwin}
              conditions={conditions}
              currentStep={currentStep}
              onFinish={finish}
              onRemoveCondition={(condition) => setConditions(conditions.filter((c) => c.id !== condition.id))}
              onStep={setCurrentStep}
              startingStep={startingStep}
            />
          )}
        </div>
        {dialogShow && maxVisitedStep.current >= summaryStep && (
          <div
            className="absolute top-0 left-0 h-full bg-gray-200 shadow-sm"
            style={{
              width: 1,
            }}
          />
        )}
      </article>
    );
  }
  function renderConditionSelectionStep(options: {description: ReactNode; step: Step; tree?: boolean}) {
    if (biotwin == null) {
      return null;
    }
    const Component = options.tree ? ConditionTreeSelectionStep : ConditionSelectionStep;
    const {conditionTypeName, title} = stepMetadataByIndex[options.step];
    return (
      <>
        {renderHeader({
          description: options.description,
          title: `Step ${options.step - startingStep + 1}: ${title ?? conditionTypeName}`,
        })}
        <Component
          biotwin={biotwin}
          conditions={conditions}
          conditionTypeName={conditionTypeName}
          key={options.step}
          onAdd={(condition) => {
            if (isDemo && conditions.length === 3) {
              toastError('You can only add up to 3 features in the demo.');
            } else {
              const preSelected = conditions.find((c) => c.id === condition.id);
              if (preSelected != null) {
                confirm({
                  onConfirm: () => {
                    setConditions((conditions) => conditions.map((c) => (c.id === condition.id ? condition : c)));
                  },
                  confirmClassName: 'rounded-full',
                  confirmLabel: 'Replace',
                  title: 'Already Selected',
                  message: `A condition (${preSelected.drugName ?? preSelected.conditionName}) with same active pharmaceutical ingredient "${condition.conditionName}" is already added. Do you want to replace it?`,
                  cancelClassName: 'rounded-full bg-transparent text-muted-foreground',
                });
              } else {
                setConditions((conditions) => [...conditions, condition]);
              }
            }
          }}
          onRemove={(condition) => setConditions((conditions) => conditions.filter((c) => c.id !== condition.id))}
        />
        {renderFooter({
          step: options.step,
        })}
      </>
    );
  }
  function renderFooter(options: {step: Step}) {
    return (
      <footer className="flex items-center p-6 gap-4 bg-zinc-100 rounded-b-[inherit]">
        {hasPrev(options.step) && (
          <Button
            onClick={() => prev()}
            type="button"
            variant="text"
            className="rounded-full"
            startIcon={<Icon icon={'arrow-left'} />}
          >
            {'Back'}
          </Button>
        )}
        <Button
          className={cn('ml-auto', 'rounded-full')}
          onClick={() => next()}
          type="button"
          variant="warning"
          endIcon={<Icon icon={'arrow-right'} />}
        >
          {options.step === Step.Completed - 1 ? 'Finish' : 'Next'}
        </Button>
      </footer>
    );
  }
  function renderHeader(options: {description: ReactNode; title: ReactNode}) {
    return (
      <header className="relative flex items-center bg-zinc-200 p-6 rounded-t-[inherit]">
        <InfoSection
          className="text-left items-start"
          title={options.title}
          message={options.description}
          messageClassName="mb-0"
        />
        <Button
          className={cn('absolute top-0 right-0 m-4', 'rounded-full')}
          onClick={() => close()}
          type="button"
          variant="ghost"
          startIcon={<Icon icon={'times'} />}
          aria-label={'Close'}
          size="icon"
        />
      </header>
    );
  }
  function close() {
    if (biotwin == null) {
      onCancel({});
    } else if (biotwin.name === initialBiotwin?.name && !isCompleteBiotwin(biotwin)) {
      onCancel({});
    } else {
      confirm({
        onConfirm: () => {
          if (conditions.length > 0) {
            updateBulkBiotwinConditions({
              userId: biotwin!.id,
              added: conditions.map((c) => ({
                conditionId: c.id,
                drugId: c.drugId,
              })),
            })
              .unwrap()
              .then(() =>
                onFinish({
                  biotwin: biotwin,
                }),
              )
              .catch(toastError);
          } else {
            onFinish({
              biotwin: biotwin,
            });
          }
        },
        confirmClassName: 'rounded-full',
        confirmLabel: 'Save and Close',
        className: 'w-[30rem]',
        title: 'Close Biotwin Wizard',
        message: 'Please confirm that you want to close the wizard.',
        onCancel: () => {
          if (biotwin == null) {
            onCancel({});
          } else if (initialBiotwin == null) {
            deleteBiotwin({
              id: biotwin.id,
            })
              .unwrap()
              .catch(toastError)
              .finally(() =>
                onCancel({
                  biotwin: biotwin,
                }),
              );
          } else {
            updateBiotwin({
              id: biotwin.id,
              changes: initialBiotwin,
            })
              .unwrap()
              .catch(toastError)
              .finally(() =>
                onCancel({
                  biotwin: initialBiotwin,
                }),
              );
          }
        },
        cancelClassName: 'rounded-full bg-transparent text-muted-foreground',
        cancelLabel: 'Discard Changes',
      });
    }
  }
  function hasPrev(step: Step) {
    return step > startingStep;
  }
  function prev() {
    setCurrentStep(currentStep - 1);
  }
  function next() {
    if (currentStep === Step.Completed - 1) {
      finish();
    } else {
      setCurrentStep(currentStep + 1);
    }
  }
  function finish() {
    updateBulkBiotwinConditions({
      userId: biotwin!.id,
      added: conditions.map((c) => ({
        conditionId: c.id,
        drugId: c.drugId,
      })),
    })
      .unwrap()
      .then(() => setCurrentStep(Step.Completed))
      .catch(toastError);
  }
};

// Summary
// =====================================================================================================================

interface SummaryProps {
  biotwin?: Biotwin;
  conditions: Condition[];
  currentStep: Step;
  onFinish: () => void;
  onRemoveCondition: (condition: Condition) => void;
  onStep: (step: Step) => void;
  startingStep: Step;
}
const Summary = ({
  biotwin,
  conditions,
  currentStep,
  onFinish,
  onRemoveCondition,
  onStep,
  startingStep,
}: SummaryProps) => {
  return (
    <div className="h-full flex flex-col">
      <header className="py-6 px-4 shrink-0 border-b border-0">
        <h5 className="m-0 text-base">Summary</h5>
      </header>
      <div className="flex-1 flex flex-col gap-4 min-h-0 overflow-auto py-6 pl-4 pr-6">
        {Object.values(Step)
          .filter((step): step is Step => typeof step === 'number')
          .filter((step) => step >= startingStep)
          .map((step) => {
            const {conditionTypeName, title} = stepMetadataByIndex[step];
            if (step === Step.BiotwinName) {
              return renderSection({
                step,
                title,
                content: biotwin?.name,
              });
            }
            if (step === Step.BiotwinBasics) {
              return renderSection({
                step,
                title,
                content: biotwin?.age != null && (
                  <BiotwinBasicsViewer
                    className="flex gap-1"
                    short
                    tagClassName="bg-transparent p-0 justify-start"
                    value={biotwin}
                  />
                ),
              });
            }
            if (step === Step.Completed) {
              return null;
            }
            return renderSection({
              step,
              title,
              content: renderConditions(conditionTypeName),
            });
          })}
      </div>
      {biotwin?.age != null && (
        <footer className="py-2 px-4 border-t border-0">
          <Button
            className={cn('w-full text-muted-foreground', 'rounded-full')}
            onClick={() => onFinish()}
            size="sm"
            variant="ghost"
            type="button"
          >
            {currentStep === Step.Completed - 1 ? 'Finish' : 'Skip Remaining Steps'}
          </Button>
        </footer>
      )}
    </div>
  );
  function renderSection({step, title, content}: {step: Step; title: string; content: ReactNode}) {
    const visitable = !isCompleteBiotwin(biotwin) ? step <= Step.BiotwinBasics : true;
    return (
      <section className="flex items-start gap-2" key={step}>
        <div
          className={cn({
            'text-green-600': currentStep > step,
            'text-cyan-600': currentStep === step,
          })}
        >
          {currentStep > step && <Icon icon="check-circle" />}
          {currentStep === step && <Icon icon="minus-circle" />}
          {currentStep < step && <Icon icon="circle" />}
        </div>
        <div className="flex-1 min-w-0">
          <header
            className={cn('mb-2', {
              'text-green-600': currentStep > step,
              'text-cyan-600': currentStep === step,
            })}
          >
            <h6
              className={cn('m-0 text-sm font-normal', {
                'hover:underline cursor-pointer': visitable,
                'cursor-not-allowed': !visitable,
              })}
              id={`biotwin-wizard-summary-step-${step}-title`}
              onClick={() => visitable && onStep(step)}
            >
              Step {step - startingStep + 1}: {title}
              {!isCompleteBiotwin(biotwin) && step > Step.BiotwinBasics && (
                <ContentHints side={'top'} target={`#biotwin-wizard-summary-step-${step}-title`}>
                  <Icon icon={'exclamation-triangle'} /> {'You must enter biotwin basics first.'}
                </ContentHints>
              )}
            </h6>
          </header>
          <div className="text-sm">{content}</div>
        </div>
      </section>
    );
  }
  function renderConditions(conditionTypeName: string) {
    return (
      <>
        {conditions
          .filter((condition) => condition.conditionTypeName === conditionTypeName)
          .map((condition) => {
            const nameContainerId = `biotwin-wizard-selected-condition-${condition.id}`;
            return (
              <div className="flex items-center gap-4" key={condition.id}>
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <ConditionTypeTag
                    className="w-0 h-0 p-1 shrink-0"
                    conditionTypeName={condition.conditionTypeName}
                    children={''}
                  />
                  <div
                    className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap min-w-0 text-sm"
                    id={nameContainerId}
                  >
                    <span>{condition.drugName ?? condition.conditionName}</span>{' '}
                    {condition.drugId != null && (
                      <span className="text-muted-foreground">({condition.conditionName})</span>
                    )}
                  </div>
                  <ContentHints
                    shouldShow={(e) => {
                      if (e.target instanceof HTMLElement) {
                        const child = e.target.querySelector('span');
                        if (child instanceof HTMLElement) {
                          const {width: childWidth} = child.getBoundingClientRect();
                          const {width: targetWidth} = e.target.getBoundingClientRect();
                          if (childWidth <= targetWidth) {
                            return false;
                          }
                        }
                      }
                    }}
                    side="top"
                    target={`#${nameContainerId}`}
                  >
                    {condition.drugName ?? condition.conditionName}
                  </ContentHints>
                </div>
                <Button
                  className={cn(undefined, 'rounded-full')}
                  onClick={() => onRemoveCondition(condition)}
                  size="sm"
                  type="button"
                  variant="text"
                  startIcon={<Icon icon={'trash'} />}
                  aria-label={'Delete'}
                />
              </div>
            );
          })}
      </>
    );
  }
};

// ConditionTreeSelectionStep
// =====================================================================================================================

interface ConditionTreeSelectionStepProps {
  biotwin: Biotwin;
  conditions: Condition[];
  conditionTypeName: string;
  onAdd: (condition: Condition) => void;
  onRemove: (condition: Condition) => void;
}
const ConditionTreeSelectionStep = ({
  biotwin,
  conditions,
  conditionTypeName,
  onAdd,
  onRemove,
}: ConditionTreeSelectionStepProps) => {
  const id = useId();
  const {data: conditionTypes, isLoading: isLoadingGetConditionTypes} = useGetConditionTypesQuery();
  const conditionType = conditionTypes?.find((conditionType) => conditionType.conditionTypeName === conditionTypeName);
  const [searchTerm, setSearchTerm] = useState('');
  const {data: allConditions = [], isLoading: isLoadingGetConditions} = useGetConditionsQuery(
    {
      conditionTypeId: conditionType?.id,
      includeGroups: true,
      pageNumber: 1,
      pageSize: 1000,
      searchTerm: '',
      userId: biotwin.id,
    },
    {
      skip: conditionType == null,
    },
  );
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean | undefined>>({});
  const treeNodes = useMemo(() => buildTreeNodes(), [allConditions]);
  const virtualTreeNodes = useMemo(() => buildVirtualTreeNodes(), []);
  const isLoading = isLoadingGetConditions || isLoadingGetConditionTypes;
  return (
    <div className="flex-1 flex flex-col p-6 min-h-0">
      <InputGroup position="right" className="w-full mb-4">
        <Input
          autoFocus
          className="w-full"
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search..."
          value={searchTerm}
        />
        <Icon icon="search" />
      </InputGroup>
      {isLoading && (
        <HierarchyTable
          className={cn(undefined, 'flex-1 min-h-0')}
          key={id}
          height="100%"
          nodes={virtualTreeNodes}
          columns={[
            {
              render: () => <LoadingSkeleton height="1rem" width="100%" />,
              className: 'py-0',
              key: 'conditionName',
              header: 'Condition Name',
              style: {
                height: 42,
              },
            },
          ]}
        />
      )}
      {!isLoading && (
        <HierarchyTable
          className={cn(undefined, 'flex-1 min-h-0')}
          key={id}
          expanded={expandedKeys}
          filter={searchTerm}
          onExpandedChange={(e) => setExpandedKeys(e)}
          onRowAction={(gridRow, rowEvent) => {
            const target = rowEvent.target;
            if (
              target instanceof HTMLButtonElement ||
              (target instanceof HTMLElement && target.closest('button') != null)
            ) {
              return;
            }
            const condition = gridRow.data as Condition;
            if (condition.isGroup || !!gridRow.children?.length) {
              const key = gridRow.key as string;

              setExpandedKeys({
                ...expandedKeys,
                [key]: !expandedKeys[key] || undefined,
              });
            } else {
              if (conditions.some((c) => isEqualConditions(c, condition))) {
                onRemove(condition);
              } else {
                onAdd(condition);
              }
            }
          }}
          rowClassName={() => ({
            'cursor-pointer': true,
          })}
          height="100%"
          nodes={treeNodes}
          columns={[
            {
              render: (node) => {
                const condition = node.data as Condition;
                return (
                  <>
                    <TextMatch matcher={searchTerm} text={condition.drugName ?? condition.conditionName} />{' '}
                    {condition.drugId != null && (
                      <span className={'text-muted-foreground'}>({condition.conditionName})</span>
                    )}
                  </>
                );
              },
              className: 'py-0',
              expander: true,
              key: 'conditionName',
              header: 'Condition Name',
              style: {
                height: 42,
              },
            },
            {
              render: (row) => {
                const condition = row.data as Condition;
                if (condition.isGroup || (row.children?.length ?? 0) > 0) {
                  return null;
                }
                if (conditions.some((c) => isEqualConditions(c, condition))) {
                  return (
                    <Button
                      className={cn('text-muted-foreground py-2', 'rounded-full')}
                      onClick={() => onRemove(condition)}
                      size="sm"
                      type="button"
                      variant="outline"
                      startIcon={<Icon icon={'trash'} />}
                    >
                      {'Remove'}
                    </Button>
                  );
                } else {
                  return (
                    <Button
                      className={cn('py-2', 'rounded-full')}
                      onClick={() => onAdd(condition)}
                      size="sm"
                      type="button"
                      startIcon={<Icon icon={'plus'} />}
                    >
                      {'Add'}
                    </Button>
                  );
                }
              },
              className: 'w-[10rem] py-0 whitespace-nowrap text-right',
              key: 'id',
            },
          ]}
        />
      )}
    </div>
  );
  function buildVirtualTreeNodes() {
    return Array.from({
      length: 50,
    }).map((_, index) => ({
      children: [],
      data: null,
      key: -index,
      label: '',
    }));
  }
  function buildTreeNodes() {
    const rootConditions = allConditions.filter((condition) => condition.parentLabelId == null);
    const rootNodes = rootConditions.map((rootCondition) => buildTreeNode(rootCondition));
    rootNodes.forEach((rootNode) => {
      if (rootNode.children?.length) {
        rootNode.children = getDescendentNodes(rootNode).filter((node) => !node.data.isGroup);
        rootNode.children.forEach((node) => {
          node.children = [];
        });
        rootNode.children.sort((a, b) => {
          const aName = a.data.drugName ?? a.data.conditionName;
          const bName = b.data.drugName ?? b.data.conditionName;
          return aName.localeCompare(bName);
        });
      }
    });
    return rootNodes;
  }
  function buildTreeNode(condition: Condition): HierarchyNode {
    const children = allConditions.filter((c) => c.parentLabelId === condition.id);
    return {
      children: children.map((child) => buildTreeNode(child)),
      data: condition,
      key: condition.id,
      label: condition.drugName ?? condition.conditionName,
    };
  }
  function getDescendentNodes(node: HierarchyNode): HierarchyNode[] {
    return [...(node.children ?? []), ...(node.children?.flatMap((child) => getDescendentNodes(child)) ?? [])];
  }
};

// ConditionSelectionStep
// =====================================================================================================================

interface ConditionSelectionStepProps {
  biotwin: Biotwin;
  conditions: Condition[];
  conditionTypeName: string;
  onAdd: (condition: Condition) => void;
  onRemove: (condition: Condition) => void;
}
const ConditionSelectionStep = ({
  biotwin,
  conditions,
  conditionTypeName,
  onAdd,
  onRemove,
}: ConditionSelectionStepProps) => {
  const isAdmin = useIsAdmin();
  const {data: conditionTypes} = useGetConditionTypesQuery();
  const conditionType = conditionTypes?.find((conditionType) => conditionType.conditionTypeName === conditionTypeName);
  const [searchTerm, searchTermDebounced, setSearchTerm] = useDebouncedState('', 300);
  const [getConditionsDataCount] = useLazyGetConditionsDataCountQuery();
  const [getConditions] = useLazyGetConditionsQuery();
  const {items, isLoading, loadingCount, onRangeChange} = useVirtualLazyLoad<
    Condition & {
      dataKey: string;
    },
    Pick<Condition, 'id'> & {
      dataKey: string;
    }
  >({
    getItems: useCallback(
      ({pageNumber, pageSize}) =>
        getConditions({
          conditionTypeId: conditionType?.id,
          pageNumber,
          pageSize,
          searchTerm: searchTermDebounced,
          searchTermMatchMode: isAdmin ? MatchMode.Similarity : MatchMode.Contains,
          userId: biotwin.id,
        })
          .unwrap()
          .then((data) =>
            data.map((condition) => ({
              ...condition,
              dataKey: `${condition.id}-${condition.drugId}`,
            })),
          ),
      [biotwin, conditionType, getConditions, searchTermDebounced],
    ),
    getItemsCount: useCallback(
      () =>
        getConditionsDataCount({
          conditionTypeId: conditionType?.id,
          searchTerm: searchTermDebounced,
          searchTermMatchMode: isAdmin ? MatchMode.Similarity : MatchMode.Contains,
          userId: biotwin.id,
        }).unwrap(),
      [biotwin, conditionType, getConditionsDataCount, searchTermDebounced],
    ),
    pageSize: 30,
    skip: conditionType == null,
    virtualItem: useCallback(
      (data: {index: number}) => ({
        id: -data.index,
        dataKey: `-${data.index}`,
      }),
      [],
    ),
  });
  const selectedConditionDataKeys = useMemo(
    () => conditions.map((condition) => `${condition.id}-${condition.drugId}`),
    [conditions],
  );
  return (
    <div className="flex-1 flex flex-col p-6 min-h-0">
      <InputGroup position="right" className="w-full mb-4">
        <Input
          autoFocus
          className="w-full"
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search..."
          value={searchTerm}
        />
        {isLoading && <Icon icon="spinner" spin />}
        {!isLoading && <Icon icon="search" />}
      </InputGroup>
      <DataGrid
        className={cn(undefined, 'flex-1 min-h-0')}
        getRowId={(item) =>
          selectedConditionDataKeys.includes(item.dataKey) ? `${item.dataKey}-selected` : item.dataKey
        }
        key={loadingCount}
        onRowAction={(gridRow, rowEvent) => {
          const target = rowEvent.target;
          if (
            target instanceof HTMLButtonElement ||
            (target instanceof HTMLElement && target.closest('button') != null)
          ) {
            return;
          }
          const row = gridRow as Condition | Pick<Condition, 'id'>;
          if ('conditionTypeName' in row) {
            if (conditions.some((condition) => isEqualConditions(condition, row))) {
              onRemove(row);
            } else {
              onAdd(row);
            }
          }
        }}
        rowClassName={(row) => ('conditionTypeName' in row ? 'cursor-pointer' : 'pointer-events-none')}
        virtual={{
          itemHeight: 42,
          onRangeChange: onRangeChange,
        }}
        height="100%"
        rows={items ?? []}
        columns={[
          {
            render: (row: Condition | Pick<Condition, 'id'>) => {
              if ('conditionName' in row) {
                const text = row.drugName ?? row.conditionName;
                return (
                  <>
                    <TextMatch
                      matcher={getTextMatchNormalizedMatcher(searchTermDebounced)}
                      referenceText={getTextMatchNormalizedText(text)}
                      text={text}
                    />{' '}
                    {row.drugId != null && <span className={'text-muted-foreground'}>({row.conditionName})</span>}
                  </>
                );
              } else {
                return <LoadingSkeleton height="1rem" width="100%" />;
              }
            },
            className: 'w-full py-0',
            key: 'conditionName',
            header: 'Condition Name',
            style: {
              height: 42,
            },
          },
          {
            render: (row: Condition | Pick<Condition, 'id'>) => {
              if ('conditionTypeName' in row) {
                if (conditions.some((condition) => isEqualConditions(condition, row))) {
                  return (
                    <Button
                      className={cn('text-muted-foreground py-2', 'rounded-full')}
                      onClick={() => onRemove(row)}
                      size="sm"
                      type="button"
                      variant="outline"
                      startIcon={<Icon icon={'trash'} />}
                    >
                      {'Remove'}
                    </Button>
                  );
                } else {
                  return (
                    <Button
                      className={cn('py-2', 'rounded-full')}
                      onClick={() => onAdd(row)}
                      size="sm"
                      type="button"
                      startIcon={<Icon icon={'plus'} />}
                    >
                      {'Add'}
                    </Button>
                  );
                }
              } else {
                return <LoadingSkeleton height="1rem" width="100%" />;
              }
            },
            className: 'py-0 whitespace-nowrap text-right',
            key: 'id',
          },
        ]}
      />
    </div>
  );
};
function isEqualConditions(c1: Condition, c2: Condition) {
  return c1.id === c2.id && c1.drugId === c2.drugId;
}
